import React from 'react';
import PropTypes from 'prop-types';

import * as formatters from '@zr/common/js/profile/formatters';
import ExpandableText from '@zr/components/ExpandableText';

import EducationSection from './EducationSection';
import ExperienceSection from './ExperienceSection';
import LicensesCertsSection from './LicensesCertsSection';

import './PreviewProfile.less';

const getHeadlineLocationText = (headline, location) => {
  if (headline && location) {
    return `${headline} in ${location}`;
  }
  return headline || location || '';
};

export function PreviewProfile({ headingLevel, profileData }) {
  const Heading = headingLevel;

  return (
    <div className="PreviewProfile">
      <div className="side_content content_column">
        {(!!profileData.name || !!profileData.headline || !!profileData.location) && (
          <section className="about_me employer_profile_section">
            {!!profileData.name && <Heading className="name">{profileData.name}</Heading>}
            {(!!profileData.headline || !!profileData.location) && (
              <p className="headline_location">{getHeadlineLocationText(profileData.headline, profileData.location)}</p>
            )}
          </section>
        )}

        {(!!profileData.desired_salary
          || !!profileData.experience
          || !!profileData.education
          || !!profileData.industry
          || !!profileData.country) && (
          <section className="more_info employer_profile_section">
            <dl>
              {!!profileData.experience && (
                <div className="row">
                  <dt className="term">Experience</dt>
                  <dd className="detail">{formatters.formatExperience(profileData.experience)}</dd>
                </div>
              )}

              {!!profileData.education && (
                <div className="row">
                  <dt className="term">Education</dt>
                  <dd className="detail">{formatters.formatEducation(profileData.education)}</dd>
                </div>
              )}

              {!!profileData.desired_salary && (
                <div className="row">
                  <dt className="term">Desired Salary</dt>
                  <dd className="detail">{formatters.formatSalary(profileData.desired_salary)}</dd>
                </div>
              )}

              {!!profileData.industry && (
                <div className="row">
                  <dt className="term">Industry</dt>
                  <dd className="detail">{profileData.industry}</dd>
                </div>
              )}

              {!!profileData.country && (
                <div className="row">
                  <dt className="term">Country</dt>
                  <dd className="detail">{profileData.country}</dd>
                </div>
              )}
            </dl>
          </section>
        )}

        {(!!profileData.email_address || !!profileData.phone_number) && (
          <section className="contact_info employer_profile_section">
            <Heading className="title">Contact Information</Heading>
            {!!profileData.email_address && <p className="email_address">{profileData.email_address}</p>}
            {!!profileData.phone_number && <p className="phone_number">{profileData.phone_number}</p>}
          </section>
        )}

        {!!profileData.skills.length && (
          <section className="skills employer_profile_section">
            <Heading className="title">Skills</Heading>
            <ol className="skills_list">
              {profileData.skills.map((skill) => (
                <li className="skill quiet_tag" key={skill.name}>{skill.name}</li>
              ))}
            </ol>
          </section>
        )}

        {(!!profileData.twitter_url || !!profileData.linkedin_url
          || !!profileData.facebook_url || !!profileData.website) && (
          <section className="websites employer_profile_section">
            <Heading className="title">Websites</Heading>
            <dl>
              {!!profileData.website && (
                <>
                  <dt className="term">Website</dt>
                  <dd className="detail">
                    <i className="icon website" />
                    <a className="url" href={profileData.website} target="_blank" rel="noopener noreferrer">
                      {profileData.website}
                    </a>
                  </dd>
                </>
              )}

              {!!profileData.linkedin_url && (
                <>
                  <dt className="term">LinkedIn</dt>
                  <dd className="detail">
                    <i className="icon linkedin" />
                    <a className="url" href={profileData.linkedin_url} target="_blank" rel="noopener noreferrer">
                      {profileData.linkedin_url}
                    </a>
                  </dd>
                </>
              )}

              {!!profileData.facebook_url && (
                <>
                  <dt className="term">Facebook</dt>
                  <dd className="detail">
                    <i className="icon facebook" />
                    <a className="url" href={profileData.facebook_url} target="_blank" rel="noopener noreferrer">
                      {profileData.facebook_url}
                    </a>
                  </dd>
                </>
              )}

              {!!profileData.twitter_url && (
                <>
                  <dt className="term">Twitter</dt>
                  <dd className="detail">
                    <i className="icon twitter" />
                    <a className="url" href={profileData.twitter_url} target="_blank" rel="noopener noreferrer">
                      {profileData.twitter_url}
                    </a>
                  </dd>
                </>
              )}
            </dl>
          </section>
        )}
      </div>

      <div className="main_content content_column">
        {!!profileData.executive_summary && (
          <section className="executive_summary employer_profile_section">
            <Heading className="title">Executive Summary</Heading>
            <ExpandableText className="description" text={profileData.executive_summary} />
          </section>
        )}

        {!!profileData.objective && (
          <section className="executive_summary employer_profile_section">
            <Heading className="title">Objective</Heading>
            <ExpandableText className="description" text={profileData.objective} />
          </section>
        )}

        {!!profileData.job_history.length && (
          <ExperienceSection headingLevel={headingLevel} experiences={profileData.job_history} />
        )}

        {!!profileData.school_history.length && (
          <EducationSection headingLevel={headingLevel} educations={profileData.school_history} />
        )}

        {!!profileData.licenses_and_certifications.length && (
          <LicensesCertsSection headingLevel={headingLevel} liccerts={profileData.licenses_and_certifications} />
        )}

        {!!profileData.achievements.length && (
          <section className="achievements employer_profile_section">
            <Heading className="title">Achievements</Heading>
            <ol>
              {profileData.achievements.map((achievement, index) => (
                <li className="item_title" key={`${index.toString()}pda`}>
                  {achievement.name}
                </li>
              ))}
            </ol>
          </section>
        )}

        {!!profileData.associations.length && (
          <section className="associations employer_profile_section">
            <Heading className="title">Associations</Heading>
            <ol>
              {profileData.associations.map((association, index) => (
                <li className="item_title" key={`${index.toString()}padrs`}>
                  {association.name}
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>
    </div>
  );
}

PreviewProfile.propTypes = {
  headingLevel: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  profileData: PropTypes.shape({
    name: PropTypes.string,
    headline: PropTypes.string,
    location: PropTypes.string,
    desired_salary: PropTypes.string,
    experience: PropTypes.string,
    education: PropTypes.string,
    industry: PropTypes.string,
    country: PropTypes.string,
    email_address: PropTypes.string,
    phone_number: PropTypes.string,
    skills: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
    website: PropTypes.string,
    twitter_url: PropTypes.string,
    linkedin_url: PropTypes.string,
    facebook_url: PropTypes.string,
    executive_summary: PropTypes.string,
    objective: PropTypes.string,
    job_history: PropTypes.arrayOf(PropTypes.object),
    school_history: PropTypes.arrayOf(PropTypes.object),
    licenses_and_certifications: PropTypes.arrayOf(PropTypes.object),
    achievements: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
    associations: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
  }).isRequired,
};

PreviewProfile.defaultProps = {
  headingLevel: 'h2',
};

export default PreviewProfile;
